import type { EpisodeAvailability } from '@anisync/types';

/**
 * Czy jest sens proponować następny odcinek — i czym on jest.
 *
 * Dotąd przycisk „Odcinek N" pokazywał się na podstawie samego `episodesTotal`
 * z katalogu, z jawnym uzasadnieniem, że sprawdzanie dostępności wymagałoby odpytania
 * wszystkich źródeł przed pokazaniem przycisku. To było prawdą przy przycisku
 * wciśniętym ręcznie: nieudane pobranie i tak kończy się tabelą prób.
 *
 * Przy **nakładce po zakończeniu odcinka** ta sama logika przestaje wystarczać.
 * Nakładka jest obietnicą: pokazuje się sama, w chwili gdy widz nie sięga po mysz,
 * i sugeruje, że jest co dalej. Zaproponowanie odcinka, którego nie ma w żadnym
 * serwisie, kończyłoby seans ekranem błędu zamiast ekranem końca serii — a to gorsze
 * niż brak nakładki.
 *
 * Dane bierzemy z `GET /catalogue/titles/:id/episodes`, czyli z tego samego miejsca,
 * z którego siatka odcinków wie, co jest do wzięcia. `sources: []` znaczy „nikt tego
 * nie ma": tak samo oznaczone są luki w niekompletnej serii.
 */

export interface NextEpisode {
  number: number;
  /**
   * Materiał już u nas, gotowy do odtworzenia.
   *
   * Rozróżnienie ma znaczenie dla treści nakładki: „Odtwórz" znaczy natychmiast,
   * a „Przygotuj i odtwórz" znaczy kilka minut pobierania. Obiecanie pierwszego,
   * gdy zachodzi drugie, to ta sama klasa błędu co nakładka bez materiału.
   */
  ready: boolean;
  /** Czy któreś źródło ma wyłącznie wersję zapasową (angielskie napisy). */
  fallbackOnly: boolean;
}

/**
 * Następny odcinek do zaproponowania albo `null`.
 *
 * `null` znaczy „nie proponuj": koniec serii, luka w wydaniach albo brak danych
 * o dostępności. We wszystkich trzech przypadkach cisza jest właściwą odpowiedzią.
 */
export function pickNextEpisode(
  episodes: EpisodeAvailability[],
  current: number | null,
  episodesTotal: number | null,
): NextEpisode | null {
  if (current === null || !Number.isInteger(current) || current < 0) return null;

  const next = current + 1;
  // Poza deklarowaną długością serii nie ma czego proponować, nawet gdyby jakieś
  // źródło zgłaszało numer wyżej — to zwykle odcinek specjalny albo błąd wydawcy.
  if (episodesTotal !== null && next > episodesTotal) return null;

  const entry = episodes.find((candidate) => candidate.episodeNumber === next);
  if (entry === undefined) return null;

  const ready = entry.mediaFileId !== null && entry.mediaState === 'ready';
  // Materiał, który już mamy, jest wystarczającym powodem: nie potrzebuje źródła,
  // bo leży na dysku. Bez tego warunku odcinek pobrany, ale usunięty ze źródła,
  // przestawał być proponowany.
  if (!ready && entry.sources.length === 0) return null;

  return {
    number: next,
    ready,
    fallbackOnly: entry.sources.length > 0 && entry.sources.every((source) => source.fallback),
  };
}
